import React, { useState, useRef } from 'react';

const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];

// Add these predefined ranges
const COMMON_RANGES = {
  'Pocket Pairs': new Set(['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', '66', '55', '44', '33', '22']),
  'Broadway': new Set(['AKs', 'AQs', 'AJs', 'ATs', 'KQs', 'KJs', 'KTs', 'QJs', 'QTs', 'JTs',
                      'AKo', 'AQo', 'AJo', 'ATo', 'KQo', 'KJo', 'KTo', 'QJo', 'QTo', 'JTo']),
  'Premium': new Set(['AA', 'KK', 'QQ', 'JJ', 'AKs', 'AKo', 'AQs', 'AQo'])
};

interface RangeSelectorProps {
  onRangeChange: (range: Set<string>) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const RangeSelector: React.FC<RangeSelectorProps> = ({ onRangeChange, isOpen, onClose }) => {
  const [selectedHands, setSelectedHands] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [isAdding, setIsAdding] = useState(true);
  const gridRef = useRef<HTMLDivElement>(null);

  const getHandString = (rank1: string, rank2: string, suited: boolean) => {
    const r1idx = RANKS.indexOf(rank1);
    const r2idx = RANKS.indexOf(rank2);
    if (r1idx === r2idx) return `${rank1}${rank2}`; // Pair
    if (r1idx < r2idx) return suited ? `${rank1}${rank2}s` : `${rank1}${rank2}o`;
    return suited ? `${rank2}${rank1}s` : `${rank2}${rank1}o`;
  };

  const handleMouseDown = (rank1: string, rank2: string, suited: boolean) => {
    const hand = getHandString(rank1, rank2, suited);
    const newIsAdding = !selectedHands.has(hand);
    setIsAdding(newIsAdding);
    setIsSelecting(true);
    
    const newSelected = new Set(selectedHands);
    if (newIsAdding) {
      newSelected.add(hand);
    } else {
      newSelected.delete(hand);
    }
    setSelectedHands(newSelected);
    onRangeChange(newSelected);
  };

  const handleMouseEnter = (rank1: string, rank2: string, suited: boolean) => {
    if (!isSelecting) return;
    
    const hand = getHandString(rank1, rank2, suited);
    const newSelected = new Set(selectedHands);
    
    if (isAdding) {
      newSelected.add(hand);
    } else {
      newSelected.delete(hand);
    }
    
    setSelectedHands(newSelected);
    onRangeChange(newSelected);
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="range-selector-overlay">
      <div className="range-selector-modal">
        <h2>Select Range</h2>
        
        {/* Add quick range buttons */}
        <div className="quick-ranges">
          {Object.entries(COMMON_RANGES).map(([name, range]) => (
            <button
              key={name}
              onClick={() => {
                setSelectedHands(new Set(range));
                onRangeChange(new Set(range));
              }}
            >
              {name}
            </button>
          ))}
        </div>

        <div 
          className="range-grid" 
          ref={gridRef}
          onMouseLeave={() => setIsSelecting(false)}
          onMouseUp={handleMouseUp}
        >
          {/* Header row */}
          <div className="range-corner" />
          <div className="range-header-row">
            {RANKS.map(rank => (
              <div key={rank} className="range-header">{rank}</div>
            ))}
          </div>

          {/* Grid cells */}
          {RANKS.map((rank1, i) => (
            <div key={rank1} className="range-row">
              <div className="range-header">{rank1}</div>
              {RANKS.map((rank2, j) => {
                const isPair = i === j;
                const isSuited = i > j;
                const hand = getHandString(rank1, rank2, isSuited);
                const isSelected = selectedHands.has(hand);

                return (
                  <div
                    key={`${rank1}${rank2}${isSuited ? 's' : 'o'}`}
                    className={`range-cell ${isSelected ? 'selected' : ''} ${
                      isPair ? 'pair' : isSuited ? 'suited' : 'offsuit'
                    }`}
                    onMouseDown={() => handleMouseDown(rank1, rank2, isSuited)}
                    onMouseEnter={() => handleMouseEnter(rank1, rank2, isSuited)}
                  >
                    {hand}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="range-controls">
          <button onClick={() => setSelectedHands(new Set())}>Clear</button>
          <button onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}; 